import React, { useEffect, useState } from "react";
import { catchException } from "redux/actions/common";
import { IFormField } from "utils/form-utils";
import { IImportError, importUtils } from "utils/import-utils";
import { StorageProviderField, storageTypeToNameMap, storageProviderTypeToRegionFieldLabelMap, storageTypeToSecretFieldLabelMap, storageUtils } from "utils/storage-utils";
import { FormFieldError } from "components/shared-ui/form/form-field-error/form-field-error";
import { FormFieldLabel } from "components/shared-ui/form/form-field-label/form-field-label";
import { FormFieldValue } from "components/shared-ui/form/form-field-value/form-field-value";
import { FormField } from "components/shared-ui/form/form-field/form-field";
import { FormSubSection } from "components/shared-ui/form/form-sub-section/form-sub-section";
import TextInput from "components/shared-ui/input/text-input/text-input";
import { Select } from "components/shared-ui/select";
import SidePanelSection from "components/shared-ui/side-panel-section/side-panel-section";
import { ImportResultDialog } from "components/import-bzs-configuration/dialogs/import-result-dialog/import-result-dialog";
import { ImportButton } from "components/import-bzs-configuration/import-button/import-button";
import { RegionSelect } from "components/shard-clusters/region-select/region-select";
import { TestConnection } from "../test-connection/test-connection";
import { IShardRepositoryTestConnectionRequest, StorageType } from "api/entities/shard-repository";
import { IFile } from "api/entities/file";
import styles from "./shard-repository-section.module.scss";

export interface IShardRepositoryTypes {
  storeContent: boolean;
  storeMetadata: boolean;
  storeKey: boolean;
}

export interface IShardRepositoryForm {
  storageType: IFormField<StorageType>;
  region: IFormField<string>;
  isCustomRegion: IFormField<boolean>;
  endpointURL: IFormField<string>;
  bucket: IFormField<string>;
  account_name: IFormField<string>;
  container: IFormField<string>;
  project_id: IFormField<string>;
  key: IFormField<string>;
  secret: IFormField<string>;
  client_email: IFormField<string>;
  gcsConfigProps: IFormField<IGCSConfigProps>;
  sasUri: IFormField<string>;
}

export interface IShardRepositoryFormValue extends IShardRepositoryTestConnectionRequest {
  isCustomRegion: boolean;
  gcsConfigProps: IGCSConfigProps;
}

export interface IShardRepositorySectionProps {
  form: IShardRepositoryForm;
  testConnectionRequest: IShardRepositoryTestConnectionRequest;
  isTestConnectionSuccess: boolean;
  onTestConnection: (testConnectionRequest: IShardRepositoryTestConnectionRequest) => void;
  showSensitiveFields: boolean;

  clearForm: () => void;
}

export interface IGCSConfigProps {
  type: "service_account";
  project_id: string;
  private_key_id: string;
  private_key: string;
  client_email: string;
  client_id: string;
  auth_uri: string;
  token_uri: string;
  auth_provider_x509_cert_url: string;
  client_x509_cert_url: string;
}

export function ShardRepositorySection(props: IShardRepositorySectionProps) {
  const { form, testConnectionRequest } = props;

  const [gcsConfigFile, setGCSConfigFile] = useState<IFile>(null);
  const [isGCSConfigDialogOpen, setIsGCSConfigDialogOpen] = useState<boolean>(false);
  const [importErrors, setImportErrors] = useState<IImportError[]>([]);

  useEffect(() => {

    let type = form.storageType.value;
    props.clearForm();

    function isFieldVisible(field: StorageProviderField) {

      let gcsHiddenSensitiveFields = [StorageProviderField.ProjectId, StorageProviderField.SecretKey, StorageProviderField.ClientEmail];
      let absHiddenSensitiveFields = [StorageProviderField.AccountName, StorageProviderField.SecretKey];

      if ((type === "GCS" && gcsHiddenSensitiveFields.includes(field))
            || (type === "ABS" && absHiddenSensitiveFields.includes(field))
      ) {
        return false;
      }

      return storageUtils.isFieldVisible(field, type, props.showSensitiveFields);
    }

    form.region.setIsVisible(isFieldVisible(StorageProviderField.Region));
    form.endpointURL.setIsVisible(isFieldVisible(StorageProviderField.EndpointURL));
    form.bucket.setIsVisible(isFieldVisible(StorageProviderField.Bucket));
    form.account_name.setIsVisible(isFieldVisible(StorageProviderField.AccountName));
    form.container.setIsVisible(isFieldVisible(StorageProviderField.Container));
    form.project_id.setIsVisible(isFieldVisible(StorageProviderField.ProjectId));
    form.key.setIsVisible(isFieldVisible(StorageProviderField.AccessKey));
    form.secret.setIsVisible(isFieldVisible(StorageProviderField.SecretKey));
    form.client_email.setIsVisible(isFieldVisible(StorageProviderField.ClientEmail));
    form.gcsConfigProps.setIsVisible(isFieldVisible(StorageProviderField.GCSConfigProps));
    form.sasUri.setIsVisible(isFieldVisible(StorageProviderField.SasUri));

    // eslint-disable-next-line
  }, [form.storageType.value]);

  useEffect(() => {
    if (gcsConfigFile) {
      gcsConfigFile.fileData.text().then(data => {
        importUtils.importGCSConfiguration(data, setImportErrors)
            .then(gcsConfigProps => {
              form.gcsConfigProps.onChange(gcsConfigProps);
            })
            .catch(catchException());
      });
    }
    // eslint-disable-next-line
  }, [gcsConfigFile]);

  useEffect(() => {
    if (importErrors.length > 0) {
      setIsGCSConfigDialogOpen(true);
    }
    // eslint-disable-next-line
  }, [importErrors]);


  return (
      <>
        <SidePanelSection
            testId="shard-repository-section"
            title="Shard Repository Configuration"
            contentClass={styles.content}
        >
          <div>
            <FormField testId="type" className={styles["field-type"]} field={form.storageType}>
              <FormFieldLabel noLeftMargin={true}>
                {form.storageType.isDisabled ? "Type:" : "Select a shard repository type:"}
              </FormFieldLabel>
              <FormFieldValue>
                <Select
                    field={form.storageType}
                    options={Object.keys(storageTypeToNameMap)
                        .map((type: StorageType) => ({
                          value: type,
                          label: storageTypeToNameMap[type]
                        }))}
                />
              </FormFieldValue>
            </FormField>
          </div>
          {form.storageType.value && (
              <FormSubSection className={styles["type-properties"]}>
                <div className={styles["type-fields-container"]}>
                  <div className={styles["type-fields"]}>

                    <FormField
                        label="Bucket Name"
                        testId="bucket-name"
                        field={form.bucket}
                    >
                      <FormFieldValue>
                        <TextInput
                            testId="bucket-name-input"
                            field={form.bucket}
                        />
                      </FormFieldValue>
                    </FormField>

                    <FormField
                        label="Container"
                        testId="container"
                        field={form.container}
                    >
                      <TextInput
                          testId="container-input"
                          field={form.container}
                      />
                    </FormField>

                    {form.region.isDisabled && (
                        <FormField
                            testId="region"
                            label="Region"
                        >
                          <TextInput
                              testId="region-input"
                              field={form.region}
                          />
                        </FormField>
                    )}
                    {!form.region.isDisabled && (
                        <FormField
                            label={`Choose a ${(storageProviderTypeToRegionFieldLabelMap[form.storageType.value] || "region").toLowerCase()}`}
                            testId="region"
                            field={form.region}
                        >
                          <RegionSelect
                              testId="region-select"
                              isCustomRegion={form.isCustomRegion.value}
                              storageType={form.storageType.value}
                              value={form.region.value}
                              onChange={(region, isCustomRegion) => {
                                form.region.onChange(region);
                                form.isCustomRegion.onChange(isCustomRegion);
                              }}
                          />
                        </FormField>
                    )}

                    <FormField
                        label="Endpoint URL"
                        testId="endpoint-url"
                        field={form.endpointURL}
                    >
                      <TextInput
                          testId="endpoint-url-input"
                          field={form.endpointURL}
                          placeholder="Endpoint URL is required for non-AWS shard repositories."
                      />
                    </FormField>

                    <FormField
                        label="Account Name"
                        testId="account-name"
                        field={form.account_name}
                    >
                      <TextInput
                          testId="account-name-input"
                          field={form.account_name}
                      />
                    </FormField>

                    <FormField
                        label="Project Id"
                        testId="project-id"
                        field={form.project_id}
                    >
                      <TextInput
                          testId="project-id-input"
                          field={form.project_id}
                      />
                    </FormField>

                    <FormField
                        label="Access Key"
                        testId="access-key"
                        field={form.key}
                    >
                      <TextInput
                          testId="access-key-input"
                          field={form.key}
                      />
                    </FormField>

                    <FormField
                        label={storageTypeToSecretFieldLabelMap[form.storageType.value]}
                        testId="secret-key"
                        field={form.secret}
                    >
                      <TextInput
                          testId="secret-key-input"
                          field={form.secret}
                      />
                    </FormField>

                    <FormField
                        label="Blob SAS URI"
                        testId="abs-blob-sas-uri"
                        field={form.sasUri}
                    >
                      <TextInput
                          testId="abs-blob-sas-input"
                          field={form.sasUri}
                      />
                    </FormField>

                    <FormField
                        label="Client Email"
                        testId="client-email"
                        field={form.client_email}
                    >
                      <TextInput
                          testId="client-email-input"
                          field={form.client_email}
                      />
                    </FormField>

                    {props.showSensitiveFields && form.storageType.value === "GCS" &&
                        <FormField
                            testId="gcs-config-file-name"
                            label="Configuration File"
                        >
                          <section className={styles["import-gcs-config-section"]}>
                            <TextInput
                                testId="gcs-config-file-name"
                                readOnly={true}
                                value={gcsConfigFile?.fileData.name ?? ''}
                            />

                            <ImportButton
                                testId='import-gcs-config-button'
                                colors="primary"
                                file={gcsConfigFile}
                                onChange={setGCSConfigFile}
                            />
                          </section>
                          {form.gcsConfigProps.error && <FormFieldError error={form.gcsConfigProps.error}/>}
                        </FormField>
                    }
                  </div>

                  {props.showSensitiveFields &&
                      <div className={styles["test-button-container"]}>
                        <TestConnection
                            testConnectionRequest={testConnectionRequest}
                            isTestConnectionSuccess={props.isTestConnectionSuccess}
                            testConnection={props.onTestConnection}
                        />
                      </div>
                  }
                </div>
              </FormSubSection>
          )}
        </SidePanelSection>

        <ImportResultDialog
            isOpen={isGCSConfigDialogOpen}
            errors={importErrors}
            onClose={() => {
              setIsGCSConfigDialogOpen(false);
              setImportErrors([]);
            }}
        />
      </>
  );
}

